import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, Search, ShoppingCart, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import SearchModal from '../common/SearchModal';
import CartPopup from '../common/CartPopup';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Determine active nav based on current path
  const getActiveNav = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/contact') return 'contact';
    if (path === '/projecten') return 'projecten';
    if (path === '/vacatures') return 'vacatures';
    if (path.startsWith('/diensten/brandblussystemen')) return 'brandblussystemen';
    if (path.startsWith('/diensten/branddetectie')) return 'branddetectie';
    if (path.startsWith('/diensten/onderhoud')) return 'onderhoud';
    if (path.startsWith('/diensten/advies')) return 'advies';
    if (path.startsWith('/diensten/noodverlichting')) return 'noodverlichting';
    if (path.startsWith('/diensten')) return 'diensten';
    return 'home';
  };

  const activeNav = getActiveNav();
  const isServicePage = ['brandblussystemen', 'branddetectie', 'onderhoud', 'advies', 'noodverlichting'].includes(activeNav);

  const navigation = [
    { id: "home", label: t('nav.home'), href: "/" },
    { 
      id: "diensten", 
      label: t('nav.diensten'), 
      href: "/diensten/brandblussystemen",
      subItems: [
        { id: "brandblussystemen", label: t('nav.brandblussystemen'), href: "/diensten/brandblussystemen" },
        { id: "branddetectie", label: t('nav.branddetectie'), href: "/diensten/branddetectie" },
        { id: "onderhoud", label: t('nav.onderhoud'), href: "/diensten/onderhoud" },
        { id: "advies", label: t('nav.advies'), href: "/diensten/advies" },
        { id: "noodverlichting", label: t('nav.noodverlichting'), href: "/diensten/noodverlichting" }
      ]
    },
    { id: "projecten", label: t('nav.projecten'), href: "/projecten" },
    { id: "vacatures", label: t('nav.vacatures'), href: "/vacatures" },
    { id: "contact", label: t('nav.contact'), href: "/contact" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleMouseEnter = (id) => {
    setOpenDropdown(id);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const toggleMobileSubmenu = (id) => {
    setMobileSubmenuOpen(mobileSubmenuOpen === id ? null : id);
  };

  const toggleLanguage = (lang) => {
    setLanguage(lang);
    setLangDropdownOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1a1a1a] shadow-lg' : 'bg-[#1a1a1a]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center group">
            <img 
              src="https://customer-assets.emergentagent.com/job_firepro-website/artifacts/zd6pwbo8_logo.png" 
              alt="Prosafe Logo" 
              className="h-16 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => {
              const isActive = item.id === activeNav || (item.id === 'diensten' && isServicePage);
              return (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => item.subItems && handleMouseEnter(item.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <a
                    href={item.href}
                    onClick={handleNavClick}
                    className={`relative font-roboto font-bold text-sm tracking-wide transition-colors duration-300 py-2 flex items-center gap-1 ${
                      isActive
                        ? 'text-[#e21b1b]'
                        : 'text-white hover:text-[#e21b1b]'
                    }`}
                  >
                    {item.label}
                    {item.subItems && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                    )}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#e21b1b]" />
                    )}
                  </a>

                  {/* Dropdown Menu */}
                  {item.subItems && (
                    <div
                      className={`absolute top-full left-0 mt-0 w-64 bg-[#1a1a1a] border border-gray-800 shadow-xl transition-all duration-300 ${
                        openDropdown === item.id
                          ? 'opacity-100 visible translate-y-0'
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      <div className="py-2">
                        {item.subItems.map((subItem) => (
                          <a
                            key={subItem.id}
                            href={subItem.href}
                            onClick={handleNavClick}
                            className={`block px-4 py-3 font-roboto text-sm transition-colors duration-200 ${
                              activeNav === subItem.id
                                ? 'text-[#e21b1b] bg-[#e21b1b]/10'
                                : 'text-gray-300 hover:text-[#e21b1b] hover:bg-[#e21b1b]/10'
                            }`}
                          >
                            {subItem.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Icons and Language Selector */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div 
              className="relative"
              onMouseEnter={() => setLangDropdownOpen(true)}
              onMouseLeave={() => setLangDropdownOpen(false)}
            >
              <button className="flex items-center space-x-1 text-white hover:text-[#e21b1b] transition-colors duration-300 p-2">
                <Globe className="w-5 h-5" />
                <span className="text-sm font-roboto font-bold uppercase">{language}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <div
                className={`absolute top-full right-0 mt-0 w-32 bg-[#1a1a1a] border border-gray-800 shadow-xl transition-all duration-300 ${
                  langDropdownOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="py-2">
                  <button
                    onClick={() => toggleLanguage('en')}
                    className={`w-full text-left px-4 py-2 font-roboto text-sm transition-colors duration-200 ${
                      language === 'en' 
                        ? 'text-[#e21b1b] bg-[#e21b1b]/10' 
                        : 'text-gray-300 hover:text-[#e21b1b] hover:bg-[#e21b1b]/10'
                    }`}
                  >
                    🇬🇧 English
                  </button>
                  <button
                    onClick={() => toggleLanguage('nl')}
                    className={`w-full text-left px-4 py-2 font-roboto text-sm transition-colors duration-200 ${
                      language === 'nl' 
                        ? 'text-[#e21b1b] bg-[#e21b1b]/10' 
                        : 'text-gray-300 hover:text-[#e21b1b] hover:bg-[#e21b1b]/10'
                    }`}
                  >
                    🇳🇱 Nederlands
                  </button>
                </div>
              </div>
            </div>

            <button 
              data-testid="cart-btn"
              onClick={() => setIsCartOpen(true)}
              className="text-white hover:text-[#e21b1b] transition-colors duration-300 p-2"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
            <button 
              data-testid="search-btn"
              onClick={() => setIsSearchOpen(true)}
              className="text-white hover:text-[#e21b1b] transition-colors duration-300 p-2"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white hover:text-[#e21b1b] transition-colors duration-300 p-2"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="bg-[#1a1a1a] border-t border-gray-800 px-4 py-4 space-y-2">
          {navigation.map((item) => {
            const isActive = item.id === activeNav || (item.id === 'diensten' && isServicePage);
            return (
              <div key={item.id}>
                <div className="flex items-center justify-between">
                  <a
                    href={item.href}
                    onClick={() => !item.subItems && handleNavClick()}
                    className={`flex-1 block py-3 px-4 font-roboto text-sm tracking-wide transition-colors duration-300 ${
                      isActive
                        ? 'text-[#e21b1b] bg-[#e21b1b]/10'
                        : 'text-white hover:text-[#e21b1b] hover:bg-[#e21b1b]/5'
                    }`}
                  >
                    {item.label}
                  </a>
                  {item.subItems && (
                    <button
                      onClick={() => toggleMobileSubmenu(item.id)}
                      className="text-white hover:text-[#e21b1b] p-3 transition-colors duration-300"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${mobileSubmenuOpen === item.id ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                
                {/* Mobile Submenu */}
                {item.subItems && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      mobileSubmenuOpen === item.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pl-6 py-2 space-y-1">
                      {item.subItems.map((subItem) => (
                        <a
                          key={subItem.id}
                          href={subItem.href}
                          onClick={handleNavClick}
                          className={`block py-2 px-4 font-roboto text-sm transition-colors duration-200 ${
                            activeNav === subItem.id
                              ? 'text-[#e21b1b]'
                              : 'text-gray-400 hover:text-[#e21b1b]'
                          }`}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Mobile Language Selector */}
          <div className="pt-4 border-t border-gray-800">
            <p className="px-4 py-2 text-gray-500 text-xs uppercase tracking-wider">Language</p>
            <div className="flex space-x-2 px-4">
              <button
                onClick={() => toggleLanguage('en')}
                className={`flex-1 py-2 px-4 font-roboto text-sm rounded transition-colors duration-200 ${
                  language === 'en' 
                    ? 'bg-[#e21b1b] text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                🇬🇧 EN
              </button>
              <button
                onClick={() => toggleLanguage('nl')}
                className={`flex-1 py-2 px-4 font-roboto text-sm rounded transition-colors duration-200 ${
                  language === 'nl' 
                    ? 'bg-[#e21b1b] text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                🇳🇱 NL
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4 pt-4 border-t border-gray-800">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="text-white hover:text-[#e21b1b] transition-colors duration-300 p-2"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setIsSearchOpen(true)}
              className="text-white hover:text-[#e21b1b] transition-colors duration-300 p-2"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </nav>
      </div>

      {/* Modals */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <CartPopup isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  );
};

export default Header;
